import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

import { flatten, type PropModel } from '../props/PropModels';
import type { Anchor, Anchors, Composition, Compositions, ModelAnchors } from './Anchors';
import { SCAFFOLDING } from './BuildingShapes';
import type { Catalogue } from './Catalogue';

/**
 * Modele budynków z plików.
 *
 * **Wczytywanie i mierzenie jest to samo, co przy rekwizytach**, więc robi to
 * `flatten` z `PropModels` — jedna funkcja, która wypieka transformacje węzłów
 * w geometrię, stawia model podstawą na zerze, centruje go na własnym rzucie
 * i normalizuje wysokość do jedynki. Druga kopia tego rachunku byłaby bliźniaczą
 * regułą, a powód, dla którego skala jest **mierzona, a nie wpisana**, jest tam
 * zapisany i obowiązuje tu tak samo: pliki wracają z eksportu w rozmiarach,
 * których nikt nie obiecywał.
 *
 * **Rodzaje bez pliku zostają sylwetkami z kodu** (`BuildingShapes.ts`) i tak
 * ma być: modele przyjeżdżają po jednym, a katalog mówi o tym wprost brakiem
 * pola `model`.
 *
 * # Który plik rysuje co, mówi katalog
 *
 * **Tej listy nie ma już po tej stronie** i to jest spłacony dług, nie
 * oszczędność. Nazwy plików leżały tutaj, w tablicy, o której nie wiedział
 * nikt poza tym plikiem — a pyta o nie także panel administratora, ustawiając
 * na bryłach ogniska i kominy. Dwie listy tego samego rozjeżdżają się przy
 * pierwszym nowym modelu, z objawem „model jest w grze, a w panelu go nie ma";
 * katalog jest jedynym miejscem, które opisuje rodzaj budynku w całości, więc
 * nazwa pliku należy do niego tak samo jak cena i warunki miejsca.
 *
 * **Plik należy do szczebla, nie do rodzaju** — i to nie jest komplikacja na
 * zapas, tylko opis tego, czym jest wioska. Obóz podniesiony o szczebel **jest
 * wioską**: ten sam wpis katalogu, ten sam budynek, inna rzecz. Wpis bez
 * `level` obowiązuje na każdym szczeblu — tak stoją drwal i kamieniołom,
 * którym awans nie zmienia dziś wyglądu.
 *
 * # Kompozycje
 *
 * Nazwa z katalogu, pod którą leży kompozycja (`compositions.json` na
 * serwerze), **jest kompozycją, a nie plikiem**: budynek składa się wtedy
 * z kilku brył ustawionych w przestrzeni heksa. Części wczytują się raz na
 * plik, ich punkty (komin, ognisko, sztandar) jadą razem z nimi, a geometria
 * scala się po materiałach — liczba draw calli rośnie z liczbą **plików**,
 * nie z liczbą części.
 */

export interface LoadedModel {
  key: string;
  /** Szczebel, na którym ten plik obowiązuje. Brak znaczy „na każdym". */
  level?: number;
  model: PropModel;
  /**
   * Punkty kompozycji, już przeliczone do przestrzeni heksa. Obecne tylko
   * przy kompozycjach — wtedy też `model` jest w przestrzeni heksa, a nie
   * znormalizowany, i nie wolno go dopasowywać do pola.
   */
  composed?: ModelAnchors;
}

/**
 * Co wczytać i pod czym to zapamiętać.
 *
 * **Rusztowanie zostaje w kodzie** i jest tu jedyną wpisaną nazwą, bo nie jest
 * rodzajem budynku: stoi **na** każdym w czasie budowy, a katalog opisuje to,
 * co da się postawić. Wpis w nim musiałby udawać budynek, którego nikt nie
 * zbuduje. Tą samą drogą nie idą też most i rekwizyty — z tego samego powodu.
 */
function wanted(catalogue?: Catalogue): { key: string; level?: number; file: string }[] {
  const files: { key: string; level?: number; file: string }[] = [
    { key: SCAFFOLDING, file: 'scaffolding-1' },
  ];

  for (const kind of catalogue?.buildings ?? []) {
    if (kind.model) files.push({ key: kind.key, file: kind.model });
    kind.levels.forEach((level, at) => {
      if (level.model) files.push({ key: kind.key, level: at + 1, file: level.model });
    });
  }

  return files;
}

/**
 * Wczytaj, co jest do wczytania.
 *
 * Model, który się nie wczytał, jest pomijany, a nie zabija wejścia do świata:
 * brak pliku znaczy tyle, że ten rodzaj zostaje przy swojej sylwetce — dokładnie
 * jak przed jego przyjazdem. Świat bez jednego budynku jest grywalny, świat,
 * który się nie otworzył, nie jest.
 *
 * **Katalog jest opcjonalny z tego samego powodu.** Gdy nie przyjechał, nie ma
 * też budynków do narysowania — a rusztowanie wczytuje się dalej, bo jego nazwa
 * jest tutaj.
 */
export async function loadBuildingModels(
  catalogue?: Catalogue,
  compositions: Compositions = {},
  anchors: Anchors = {}
): Promise<LoadedModel[]> {
  const loader = new GLTFLoader();
  const loaded: LoadedModel[] = [];

  // Jeden plik wczytuje się raz, choćby stał w pięciu kompozycjach: wspólny
  // materiał jest tym, co pozwala potem scalić geometrię.
  const files = new Map<string, Promise<PropModel>>();
  const load = (file: string) => {
    let held = files.get(file);
    if (!held) {
      held = loader.loadAsync(`/models/${file}.glb`).then((gltf) => flatten(file, gltf.scene));
      files.set(file, held);
    }
    return held;
  };

  await Promise.all(
    wanted(catalogue).map(async ({ key, level, file }) => {
      try {
        const composition = compositions[file];
        if (composition) {
          const { model, points } = await compose(file, composition, load, anchors);
          loaded.push({ key, level, model, composed: points });
        } else {
          loaded.push({ key, level, model: await load(file) });
        }
      } catch (error) {
        console.warn(`model budynku ${key} (${file}) nie wczytał się:`, error);
      }
    })
  );

  return loaded;
}

/** Rodzaje punktów, które jadą z częścią do kompozycji. */
const POINT_KINDS = ['hearth', 'smoke', 'banner', 'attach'] as const;

/**
 * Złóż budynek z części.
 *
 * Każda część to bryła po `flatten` (wysokość jeden), przeniesiona macierzą
 * położenie · obrót · skala do przestrzeni heksa. Jej punkty idą tą samą
 * macierzą; punkty kompozycji dokładają się na końcu, już w tej przestrzeni.
 */
async function compose(
  name: string,
  composition: Composition,
  load: (file: string) => Promise<PropModel>,
  anchors: Anchors
): Promise<{ model: PropModel; points: ModelAnchors }> {
  const bodies = await Promise.all(composition.parts.map((part) => load(part.shape)));

  const byMaterial = new Map<THREE.Material, THREE.BufferGeometry[]>();
  const points: Required<ModelAnchors> = { hearth: [], smoke: [], banner: [], attach: [] };
  const matrix = new THREE.Matrix4();
  const spot = new THREE.Vector3();

  composition.parts.forEach((part, index) => {
    const scale = part.scale ?? 1;
    matrix.compose(
      new THREE.Vector3(...part.at),
      new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), part.turn ?? 0),
      new THREE.Vector3(scale, scale, scale)
    );

    for (const piece of bodies[index].parts) {
      const geometry = piece.geometry.clone().applyMatrix4(matrix);
      const list = byMaterial.get(piece.material) ?? [];
      list.push(geometry);
      byMaterial.set(piece.material, list);
    }

    const own = anchors[part.shape] ?? {};
    for (const kind of POINT_KINDS) {
      for (const point of own[kind] ?? []) {
        spot.set(...point).applyMatrix4(matrix);
        points[kind].push([spot.x, spot.y, spot.z]);
      }
    }
  });

  for (const kind of POINT_KINDS) {
    points[kind].push(...((composition.anchors?.[kind] ?? []) as Anchor[]));
  }

  // Scalanie po materiale: części z jednego pliku stają się jedną siatką.
  // Geometrie o różnych atrybutach scalić się nie dadzą — wtedy zostają osobno.
  const parts: PropModel['parts'] = [];
  for (const [material, list] of byMaterial) {
    const merged = list.length > 1 ? mergeGeometries(list) : list[0];
    if (merged) {
      merged.computeBoundingSphere();
      parts.push({ geometry: merged, material });
    } else {
      for (const geometry of list) {
        geometry.computeBoundingSphere();
        parts.push({ geometry, material });
      }
    }
  }

  return { model: { name, parts, footprint: 0 }, points };
}
